import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { useHotkeys } from "react-hotkeys-hook";

import cn from "@utils/cn";

const CLASS_NAMES = cn(`
  flex
  fixed
  inset-0
  z-1000
  items-center
  justify-center
  p-4
  overflow-auto
`);

const BACKDROP_CLASS_NAMES = cn(`
  fixed
  inset-0

  bg-[rgba(10,_10,_10,_0.86)]
`);

const DIALOG_CLASS_NAMES = cn(`
  flex
  flex-col
  relative
  w-full
  max-h-[90vh]
  m-auto
  rounded-2xl
  overflow-hidden
  isolate
  mt-[2vh]
  md:mt-[10vh]

  shadow-dialog
  bg-(--primary-background-color)
  text-(--primary-foreground-color)
`);

const MODAL_WIDTH_CLASS_NAMES = {
  // xs: "md:w-[32rem]",
  sm: "md:w-[48rem]",
  md: "md:w-[64rem]",
  lg: "md:w-[80rem]",
  xl: "md:w-[96rem]",
};

const MODAL_VARIANTS = {
  initial: {
    opacity: 0,
  },
  open: {
    opacity: 1,
    transition: {
      duration: 0.25,
      ease: "easeIn",
    },
  },
  close: {
    opacity: 0,
    transition: {
      duration: 0.25,
      ease: "easeIn",
    },
  },
};

const MODAL_DIALOG_VARIANTS = {
  initial: {
    opacity: 0,
    y: "0%",
    scale: 1.15,
    transition: {
      duration: 0.1,
      ease: "easeIn",
    },
  },
  open: {
    opacity: 1,
    y: "0%",
    scale: 1,
    transition: {
      duration: 0.15,
      delay: 0.1,
      ease: "easeIn",
    },
  },
  close: {
    opacity: 0,
    y: "0%",
    scale: 1.1,
    transition: {
      duration: 0.25,
      ease: "easeIn",
    },
  },
};

const defaultProps = {
  width: "md",
};

export default function BaseModal(props) {
  const {
    children,
  } = props;

  const modal = props.mx;
  const width = props.width || defaultProps.width;

  useHotkeys("escape", modal?.close, {
    enabled: true,
    enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"],
    enableOnContentEditable: true,
    preventDefault: true,
  }, [modal]);

  const [isModalPortalCreated, setIsModalPortalCreated] = useState(false);

  useEffect(() => {
    setIsModalPortalCreated(true);
  }, []);

  const handleClickBackdrop = () => modal?.close();

  /**************************************************************************************************/

  if (!modal) return null;
  if (!isModalPortalCreated) return null;

  const widthClassName = MODAL_WIDTH_CLASS_NAMES[width];
  const dialogClassNames = cn(DIALOG_CLASS_NAMES, widthClassName);

  const Modal = (
    <AnimatePresence mode="wait">
      {modal.isOpen && (
        <motion.div
          key="modal"
          className={CLASS_NAMES}
          variants={MODAL_VARIANTS}
          initial="initial"
          animate="open"
          exit="close"
        >
          <div
            className={BACKDROP_CLASS_NAMES}
            onClick={handleClickBackdrop}
          />
          <motion.div
            key="modal-dialog"
            className={dialogClassNames}
            variants={MODAL_DIALOG_VARIANTS}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(Modal, document.getElementById("modal-root"));
}
